/**
 * Login Page
 * User authentication with email/password flow
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/common/loading-spinner';
import { useAuth, useLogin, usePasswordReset } from '@/hooks/use-auth';
import { toast } from '@/stores/ui-store';
import { Mail, Lock, ArrowLeft } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');
  
  const { isAuthenticated, isInitialized } = useAuth();
  const loginMutation = useLogin();
  const passwordResetMutation = usePasswordReset();
  
  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isInitialized && isAuthenticated) {
      const redirect = returnUrl ? decodeURIComponent(returnUrl) : '/';
      navigate(redirect, { replace: true });
    }
  }, [isAuthenticated, isInitialized, navigate, returnUrl]);
  
  // Handle password login
  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }
    
    try {
      await loginMutation.mutateAsync({ email, password });
      toast.success('Login successful');
      const redirect = returnUrl ? decodeURIComponent(returnUrl) : '/';
      navigate(redirect);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed';
      toast.error('Login failed', message);
    }
  };
  
  // Handle password reset
  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!resetEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    try {
      await passwordResetMutation.mutateAsync(resetEmail);
      setResetSent(true);
      toast.success('Reset link sent', 'Check your email for the password reset link');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send reset link';
      toast.error('Reset failed', message);
    }
  };
  
  // Loading state
  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-[#ffffff] flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading..." />
      </div>
    );
  }
  
  // Reset password form
  if (showResetForm) {
    return (
      <div className="min-h-screen bg-[#ffffff] flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-[#e5e5e5]">
          <CardHeader className="space-y-1">
            <div className="flex items-center gap-2 mb-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 -ml-2"
                onClick={() => setShowResetForm(false)}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            </div>
            <CardTitle className="text-2xl font-bold text-[#000000]">
              Reset Password
            </CardTitle>
            <CardDescription className="text-[#666666]">
              {resetSent 
                ? 'Check your email for the reset link' 
                : 'Enter your email to receive a password reset link'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {resetSent ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-[#000000] flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-8 w-8 text-[#ffffff]" />
                </div>
                <p className="text-[#333333] mb-4">
                  We've sent a password reset link to:
                </p>
                <p className="font-medium text-[#000000]">{resetEmail}</p>
                <Button
                  variant="outline"
                  className="mt-6 w-full"
                  onClick={() => {
                    setShowResetForm(false);
                    setResetSent(false);
                    setResetEmail('');
                  }}
                >
                  Back to Login
                </Button>
              </div>
            ) : (
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email" className="text-[#333333]">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666666]" />
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="name@example.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#000000] hover:bg-[#1a1a1a]"
                  disabled={passwordResetMutation.isPending}
                >
                  {passwordResetMutation.isPending ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Main login form
  return (
    <div className="min-h-screen bg-[#ffffff] flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-[#e5e5e5]">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <div className="h-12 w-12 rounded-lg bg-[#000000] flex items-center justify-center">
              <span className="text-[#ffffff] font-bold text-xl">W</span>
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center text-[#000000]">
            WillScraper
          </CardTitle>
          <CardDescription className="text-center text-[#666666]">
            Sign in to your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#333333]">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666666]" />
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[#333333]">
                  Password
                </Label>
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs text-[#666666]"
                  onClick={() => setShowResetForm(true)}
                  type="button"
                >
                  Forgot password?
                </Button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666666]" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <Button
              type="submit"
              className="w-full bg-[#000000] hover:bg-[#1a1a1a]"
              disabled={loginMutation.isPending}
            >
              {loginMutation.isPending ? (
                <LoadingSpinner size="sm" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
          
          {/* Sign Up Link */}
          <div className="mt-6 pt-4 border-t border-[#e5e5e5] text-center">
            <p className="text-sm text-[#666666]">
              Don't have an account?{' '}
              <Link
                to="/signup"
                className="text-[#000000] hover:underline font-medium"
              >
                Sign up
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
